import React, { useState } from 'react';
import axios from 'axios';
import { MessageCircle, Mail, Phone, MapPin, Send } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { WA_URL } from '../lib/utils';
import Reveal from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContatoPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSending(true);
    // Cópia automática para a caixa interna (não bloqueia o WhatsApp)
    axios.post(`${API}/contact`, { ...form, source: 'Página Contato' }).catch(() => {});
    const msg = `🟧 *Contato pelo site HM Talent*

*Nome:* ${form.name}
*E-mail:* ${form.email}
*Telefone:* ${form.phone}

*Mensagem:*
${form.message}`;
    setTimeout(() => {
      window.open(WA_URL(msg), '_blank');
      toast({ title: 'Mensagem enviada!', description: 'Direcionando para o WhatsApp.' });
      setForm({ name: '', email: '', phone: '', message: '' });
      setSending(false);
    }, 400);
  };

  return (
    <div>
      <section className="hm-gradient text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm px-6 text-center relative">
          <Reveal>
            <p className="text-sm uppercase tracking-widest text-white/85 font-bold mb-3">Fale conosco</p>
            <h1 className="text-4xl md:text-6xl font-extrabold">Entre em contato</h1>
            <p className="mt-4 text-lg text-white/95 max-w-2xl mx-auto">Estamos prontos para acelerar seu processo de recrutamento. Resposta em até 24h úteis.</p>
          </Reveal>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-hm grid lg:grid-cols-5 gap-8">
          {/* WHATSAPP em destaque */}
          <Reveal variant="slideRight" className="lg:col-span-2 hm-gradient text-white rounded-3xl p-8 card-shadow flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center mb-5 shadow-lg">
                <MessageCircle className="w-7 h-7 text-hm-orange" />
              </div>
              <p className="text-xs uppercase tracking-widest text-white/85 font-bold">Atendimento rápido</p>
              <h2 className="text-3xl font-extrabold mt-1">WhatsApp</h2>
              <p className="mt-3 text-white/95 leading-relaxed">Tire suas dúvidas, solicite propostas ou converse diretamente com nossa equipe pelo WhatsApp. Resposta imediata em horário comercial.</p>
              <a href={WA_URL()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-phone-link" className="mt-5 inline-block text-2xl font-extrabold hover:underline underline-offset-4">(11) 92089-7014</a>
            </div>
            <a href={WA_URL()} target="_blank" rel="noopener noreferrer" className="btn-hm-white mt-6">
              <MessageCircle className="w-5 h-5" /> Falar no WhatsApp
            </a>
          </Reveal>

          {/* FORMULÁRIO em destaque */}
          <Reveal variant="slideLeft" className="lg:col-span-3 bg-hm-yellow border border-amber-200 rounded-3xl p-8 card-shadow">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">Envie sua mensagem</h2>
            <p className="mt-1 text-slate-700">Nossa equipe retornará em instantes.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Input required placeholder="Seu nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 bg-white" />
              <Input required type="email" placeholder="Seu e-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 bg-white" />
              <Input placeholder="Telefone (opcional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 bg-white" />
              <Textarea required placeholder="Como podemos te ajudar?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="bg-white" />
              <button type="submit" disabled={sending} className="btn-hm disabled:opacity-60">
                {sending ? 'Enviando...' : <>Enviar mensagem <Send className="w-4 h-4" /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Outras formas de contato (reduzidas) */}
      <section className="section-padding bg-hm-yellow">
        <div className="container-hm grid md:grid-cols-3 gap-4">
          {[
            { icon: Phone, title: 'Telefone / WhatsApp', value: '(11) 92089-7014', href: WA_URL(), testid: 'contact-card-phone-link' },
            { icon: Mail, title: 'E-mail comercial', value: 'higor@hmtalent.com.br', href: 'mailto:higor@hmtalent.com.br', testid: 'contact-card-email-link' },
            { icon: MapPin, title: 'Localização', value: 'Mauá - SP | Atendimento em todo o território nacional' },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 80} variant="fadeUp" className="flex items-start gap-3 bg-white border border-amber-100 rounded-2xl p-5 hover-lift card-shadow">
              <div className="w-10 h-10 rounded-lg hm-gradient flex items-center justify-center shrink-0">
                <c.icon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-bold text-slate-900">{c.title}</h4>
                {c.href ? (
                  <a href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" data-testid={c.testid} className="text-sm text-hm-orange font-semibold break-words hover:underline">{c.value}</a>
                ) : (
                  <p className="text-sm text-slate-700 break-words">{c.value}</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ContatoPage;
